/**
 * Modern exposure calculation utilities
 * Calculates exposure on-demand from Postgres data (demand, supply, hedges)
 * No pre-calculation or storage - always fresh from source of truth
 */

import { DemandPosition } from '../services/demandService';
import { SupplyPosition } from '../services/supplyService';
import { HedgePosition } from '../services/hedgesService';
import { ExposureHorizonWindow, isWithinHorizon } from './exposureHorizon';
import { convertDateToMonthYear } from './MonthYear/monthYear';
import { Material } from '../sp-types';

// Price info from useFormulasPrices hook
export interface FormulaPriceInfo {
  price: number;           // Spot price in base currency (FX-converted)
  currency: string;        // Base currency
  originalPrice?: number;  // Original spot price before FX conversion
  originalCurrency?: string; // Original currency before FX conversion
}

export interface ExposureValue {
  // Quantity-based
  quantity: number;              // Normalized to MT
  quantityUnit: string;          // Always "MT" after normalization
  
  // Spot price used
  spotPrice: number;             // per MT, in original currency
  currency: string;              // e.g., "USD", "EUR", "CNY"
  
  // Monetary values
  valueOriginal: number;         // quantity × spotPrice (in original currency)
  valueBase: number;             // valueOriginal × fxRate (in base currency)
  
  // FX metadata
  fxRateToBase: number;          // conversion rate to base currency
  baseCurrency: string;          // account base currency (e.g., "USD")
}

export interface ExposureBreakdown {
  formulaId: string;             // e.g., "BUS" (Bottle Grade PET)
  formulaName: string;           // e.g., "Bottle Grade PET"
  
  demand: ExposureValue;
  supply: ExposureValue;
  hedges: ExposureValue;
  netExposure: ExposureValue;
  
  // Metadata
  horizonMonths: number;         // viewing horizon applied
  calculatedAt: Date;            // timestamp of calculation
  spotPriceAsOfDate: string | null; // actual date of spot prices from API
}

/**
 * Unit normalization helper
 * Converts quantity to MT (metric tons)
 * For now: assume all quantities are MT, log warnings for non-MT units
 */
const normalizeQuantityToMT = (quantity: number, uom: string | null | undefined): number => {
  if (!uom || uom === 'MT' || uom === 'mt' || uom === 'tons' || uom === 'T') {
    return quantity;
  }
  
  // Log warning for non-standard units
  console.warn(`[exposureCalculation] Non-MT unit detected: "${uom}". Assuming MT. Consider adding explicit conversion.`);
  
  // Future: Add explicit conversions
  // if (uom === 'kg' || uom === 'KG') return quantity / 1000;
  // if (uom === 'lbs' || uom === 'LBS') return quantity / 2204.62;
  
  return quantity; // Assume MT for now
};

/**
 * Helper to create ExposureValue from quantity and price info
 */
const createExposureValue = (
  quantity: number,
  priceInfo: FormulaPriceInfo,
  baseCurrency: string,
): ExposureValue => {
  // Use original price if available, otherwise use converted price
  const spotPrice = priceInfo.originalPrice || priceInfo.price;
  const currency = priceInfo.originalCurrency || priceInfo.currency;
  const priceInBase = priceInfo.price; // Already FX-converted
  
  const valueOriginal = quantity * spotPrice;
  const valueBase = quantity * priceInBase;
  
  // Calculate FX rate: valueBase / valueOriginal
  const fxRateToBase = valueOriginal !== 0 ? valueBase / valueOriginal : 1.0;
  
  return {
    quantity,
    quantityUnit: 'MT',
    spotPrice,
    currency,
    valueOriginal,
    valueBase,
    fxRateToBase,
    baseCurrency,
  };
};

/**
 * Calculate demand exposure from positions
 * Uses spot prices (not contract prices) for mark-to-market exposure
 */
export const calculateDemandExposure = (
  positions: DemandPosition[],
  pricesMap: Record<string, FormulaPriceInfo>,
  baseCurrency: string,
  formulaId?: string,
  horizonWindow?: ExposureHorizonWindow,
): ExposureValue => {
  let totalQuantity = 0;

  positions.forEach((pos) => {
    // Filter by formula if specified
    if (formulaId && pos.formula_code !== formulaId) {
      return;
    }

    // Filter by horizon if specified
    if (horizonWindow) {
      const [year, month] = pos.delivery_period.split('-').map(Number);
      if (!isWithinHorizon({ year, month }, horizonWindow)) {
        return;
      }
    }

    // Normalize quantity to MT
    const quantity = normalizeQuantityToMT(Number(pos.quantity) || 0, pos.quantity_uom);
    totalQuantity += quantity;
  });

  // Get price info from pricesMap
  const priceInfo = formulaId ? pricesMap[formulaId] : null;
  
  // Only warn once per formula (reduce console spam)
  if (!priceInfo && formulaId) {
    const warningKey = `missing_price_${formulaId}`;
    if (!(window as any)[warningKey]) {
      console.warn(`[exposureCalculation] No price found for formula: ${formulaId}. Exposure will be calculated with spot price = 0.`);
      (window as any)[warningKey] = true;
    }
  }

  // Use default price info if not found
  const defaultPriceInfo: FormulaPriceInfo = { price: 0, currency: baseCurrency };
  return createExposureValue(totalQuantity, priceInfo || defaultPriceInfo, baseCurrency);
};

/**
 * Calculate supply exposure from positions
 * Uses spot prices (not contract prices) for mark-to-market exposure
 */
export const calculateSupplyExposure = (
  positions: SupplyPosition[],
  pricesMap: Record<string, FormulaPriceInfo>,
  baseCurrency: string,
  formulaId?: string,
  horizonWindow?: ExposureHorizonWindow,
): ExposureValue => {
  let totalQuantity = 0;

  positions.forEach((pos) => {
    // Filter by formula if specified
    if (formulaId && pos.formula_code !== formulaId) {
      return;
    }

    // Filter by horizon if specified
    if (horizonWindow) {
      const [year, month] = pos.delivery_or_available_period.split('-').map(Number);
      if (!isWithinHorizon({ year, month }, horizonWindow)) {
        return;
      }
    }

    // Normalize quantity to MT
    const quantity = normalizeQuantityToMT(Number(pos.quantity) || 0, pos.quantity_uom);
    totalQuantity += quantity;
  });

  // Get price info from pricesMap
  const priceInfo = formulaId ? pricesMap[formulaId] : null;
  
  // Only warn once per formula (reduce console spam)
  if (!priceInfo && formulaId) {
    const warningKey = `missing_price_${formulaId}`;
    if (!(window as any)[warningKey]) {
      console.warn(`[exposureCalculation] No price found for formula: ${formulaId}. Exposure will be calculated with spot price = 0.`);
      (window as any)[warningKey] = true;
    }
  }

  // Use default price info if not found
  const defaultPriceInfo: FormulaPriceInfo = { price: 0, currency: baseCurrency };
  return createExposureValue(totalQuantity, priceInfo || defaultPriceInfo, baseCurrency);
};

/**
 * Calculate hedges exposure from positions
 * Hedges are VECTORIAL: long positions (+), short positions (-)
 * Uses spot prices for mark-to-market exposure
 */
export const calculateHedgesExposure = (
  positions: HedgePosition[],
  pricesMap: Record<string, FormulaPriceInfo>,
  baseCurrency: string,
  formulaId?: string,
  horizonWindow?: ExposureHorizonWindow,
): ExposureValue => {
  let totalQuantity = 0;

  positions.forEach((pos) => {
    // Filter by formula if specified
    if (formulaId && pos.formula_code !== formulaId) {
      return;
    }

    // Filter by horizon if specified
    if (horizonWindow && pos.maturity_date) {
      const maturityDate = new Date(pos.maturity_date);
      const monthYear = convertDateToMonthYear(maturityDate);
      if (!isWithinHorizon(monthYear, horizonWindow)) {
        return;
      }
    }

    // Apply position_side sign: long = +1, short = -1
    const directionMultiplier = pos.position_side === 'long' ? 1 : -1;
    const quantity = Number(pos.hedged_quantity_mt) || 0;
    
    // Normalize and apply direction
    const normalizedQuantity = normalizeQuantityToMT(quantity, null) * directionMultiplier;
    totalQuantity += normalizedQuantity;
  });

  // Get price info from pricesMap
  const priceInfo = formulaId ? pricesMap[formulaId] : null;
  
  // Only warn once per formula (reduce console spam)
  if (!priceInfo && formulaId) {
    const warningKey = `missing_price_${formulaId}`;
    if (!(window as any)[warningKey]) {
      console.warn(`[exposureCalculation] No price found for formula: ${formulaId}. Exposure will be calculated with spot price = 0.`);
      (window as any)[warningKey] = true;
    }
  }

  // Use default price info if not found
  const defaultPriceInfo: FormulaPriceInfo = { price: 0, currency: baseCurrency };
  return createExposureValue(totalQuantity, priceInfo || defaultPriceInfo, baseCurrency);
};

/**
 * Calculate complete exposure breakdown
 * Formula: Net Exposure = Demand - Supply - Hedges
 */
export const calculateExposure = (
  demandPositions: DemandPosition[],
  supplyPositions: SupplyPosition[],
  hedgePositions: HedgePosition[],
  pricesMap: Record<string, FormulaPriceInfo>,
  materialsMap: Record<string, Material>,
  baseCurrency: string,
  spotPriceAsOfDate: string | null,
  formulaId?: string,
  horizonWindow?: ExposureHorizonWindow,
  horizonMonths: number = 12,
): ExposureBreakdown => {
  const demand = calculateDemandExposure(demandPositions, pricesMap, baseCurrency, formulaId, horizonWindow);
  const supply = calculateSupplyExposure(supplyPositions, pricesMap, baseCurrency, formulaId, horizonWindow);
  const hedges = calculateHedgesExposure(hedgePositions, pricesMap, baseCurrency, formulaId, horizonWindow);

  // Calculate net exposure
  const netQuantity = demand.quantity - supply.quantity - hedges.quantity;
  
  // Get price info for net exposure calculation
  const priceInfo = formulaId ? pricesMap[formulaId] : null;
  const defaultPriceInfo: FormulaPriceInfo = { price: 0, currency: baseCurrency };
  const netExposure = createExposureValue(
    netQuantity,
    priceInfo || defaultPriceInfo,
    baseCurrency,
  );

  // Get formula name from materials map
  const material = formulaId ? materialsMap[formulaId] : null;
  const formulaName = material?.name || formulaId || 'General';

  return {
    formulaId: formulaId || 'general',
    formulaName,
    demand,
    supply,
    hedges,
    netExposure,
    horizonMonths,
    calculatedAt: new Date(),
    spotPriceAsOfDate,
  };
};

/**
 * Calculate exposure for all formulas (General view)
 * Returns a map of formulaId -> ExposureBreakdown
 * Each formula uses its own FX rate to convert to base currency
 */
export const calculateExposureByFormula = (
  demandPositions: DemandPosition[],
  supplyPositions: SupplyPosition[],
  hedgePositions: HedgePosition[],
  pricesMap: Record<string, FormulaPriceInfo>,
  materialsMap: Record<string, Material>,
  baseCurrency: string,
  spotPriceAsOfDate: string | null,
  horizonWindow?: ExposureHorizonWindow,
  horizonMonths: number = 12,
): Record<string, ExposureBreakdown> => {
  // Get all unique formula IDs
  const formulaIds = new Set<string>();
  demandPositions.forEach((pos) => formulaIds.add(pos.formula_code));
  supplyPositions.forEach((pos) => formulaIds.add(pos.formula_code));
  hedgePositions.forEach((pos) => formulaIds.add(pos.formula_code));

  // Calculate exposure for each formula (prices already FX-converted in pricesMap)
  const exposureMap: Record<string, ExposureBreakdown> = {};
  formulaIds.forEach((formulaId) => {
    exposureMap[formulaId] = calculateExposure(
      demandPositions,
      supplyPositions,
      hedgePositions,
      pricesMap,
      materialsMap,
      baseCurrency,
      spotPriceAsOfDate,
      formulaId,
      horizonWindow,
      horizonMonths,
    );
  });

  return exposureMap;
};

/**
 * Calculate total exposure across all formulas (General view aggregate)
 * Sums base currency values from all formulas (never mix currencies!)
 */
export const calculateTotalExposure = (
  demandPositions: DemandPosition[],
  supplyPositions: SupplyPosition[],
  hedgePositions: HedgePosition[],
  pricesMap: Record<string, FormulaPriceInfo>,
  materialsMap: Record<string, Material>,
  baseCurrency: string,
  spotPriceAsOfDate: string | null,
  horizonWindow?: ExposureHorizonWindow,
  horizonMonths: number = 12,
): ExposureBreakdown => {
  // Calculate exposure for each formula
  const exposureByFormula = calculateExposureByFormula(
    demandPositions,
    supplyPositions,
    hedgePositions,
    pricesMap,
    materialsMap,
    baseCurrency,
    spotPriceAsOfDate,
    horizonWindow,
    horizonMonths,
  );

  // Aggregate base currency values (quantities cannot be summed across different commodities)
  let totalDemandBase = 0;
  let totalSupplyBase = 0;
  let totalHedgesBase = 0;
  let totalNetBase = 0;

  Object.values(exposureByFormula).forEach((breakdown) => {
    totalDemandBase += breakdown.demand.valueBase;
    totalSupplyBase += breakdown.supply.valueBase;
    totalHedgesBase += breakdown.hedges.valueBase;
    totalNetBase += breakdown.netExposure.valueBase;
  });

  // Create aggregated exposure values
  // Note: quantities and spot prices are not meaningful for cross-commodity aggregation
  const createAggregateValue = (valueBase: number): ExposureValue => ({
    quantity: 0, // Not meaningful across commodities
    quantityUnit: 'MT',
    spotPrice: 0, // Not meaningful across commodities
    currency: baseCurrency,
    valueOriginal: valueBase, // Same as base for aggregate
    valueBase,
    fxRateToBase: 1.0,
    baseCurrency,
  });

  return {
    formulaId: 'general',
    formulaName: 'Overall Exposure',
    demand: createAggregateValue(totalDemandBase),
    supply: createAggregateValue(totalSupplyBase),
    hedges: createAggregateValue(totalHedgesBase),
    netExposure: createAggregateValue(totalNetBase),
    horizonMonths,
    calculatedAt: new Date(),
    spotPriceAsOfDate,
  };
};
